import { johanEstradaPublications } from './johanEstradaPublications';
import { renanQuijanoPublications } from './renanQuijanoPublications';

export type PublicationType =
  | 'article'
  | 'conference-paper'
  | 'chapter'
  | 'preprint'
  | 'report'
  | 'workshop'
  | 'thesis-doctoral'
  | 'thesis-masters'
  | 'dataset';

export type Publication = {
  id: string;
  title?: string;
  titleKey?: string;
  authors?: string;
  authorsKey?: string;
  abstract?: string;
  abstractKey?: string;
  tagsKey?: string;
  tags?: string[];
  date: string | null;
  type: PublicationType;
  venue?: string;
  publicationDetails?: string;
  category?: string;
  doi?: string;
  pdfPath?: string;
  pdfUrl?: string;
  publicAccessUrl?: string;
  researchGateUrl?: string;
  researchGateFullTextAvailable?: boolean;
};

export function getPublicationTitle(pub: Publication, t: (key: string) => string) {
  return pub.title ?? (pub.titleKey ? t(`publicaciones.${pub.titleKey}`) : '');
}

export function getPublicationAuthors(pub: Publication, t: (key: string) => string) {
  return pub.authors ?? (pub.authorsKey ? t(`publicaciones.${pub.authorsKey}`) : '');
}

export function getPublicationTypeTranslationKey(type: PublicationType) {
  if (type === 'conference-paper') return 'type_conference';
  if (type === 'thesis-doctoral') return 'type_thesis_doctoral';
  if (type === 'thesis-masters') return 'type_thesis_masters';
  if (type === 'dataset') return 'type_dataset';
  return `type_${type}`;
}

export function getPublicationTags(pub: Publication, t: (key: string) => string) {
  if (pub.tagsKey) {
    return t(`publicaciones.${pub.tagsKey}`).split(',').map(tag => tag.trim());
  }

  return (pub.tags ?? ['research']).map(tag => {
    const key = `publicaciones.tag_${tag}`;
    const translatedTag = t(key);
    return translatedTag === key ? tag.replace(/_/g, ' ') : translatedTag;
  });
}

export function formatPublicationDate(date: string | null, language: string, month: 'short' | 'long') {
  if (!date) return '';
  if (/^\d{4}$/.test(date)) return date;

  const locale = { ES: 'es-MX', EN: 'en-US', FR: 'fr-FR' }[language.toUpperCase()] ?? 'es-MX';
  return new Date(date).toLocaleDateString(locale, { year: 'numeric', month });
}

const spreadsheetPublicationRecords: Publication[] = [
  {
    "id": "castillo-002",
    "title": "Quantifying electrical network interconnectivity in polymer nanocomposites using graph theory",
    "type": "article",
    "date": "2026-10-01",
    "venue": "Composites Science and Technology",
    "authors": "Helbert Canché, I. Isaac, O. Solís-Canto, [...], Francis Aviles",
    "doi": "https://doi.org/10.1016/j.compscitech.2026.111890",
    "tags": [
      "nanocomposites",
      "materials",
      "mathematical_modeling"
    ],
    "abstractKey": "abstract_castillo_002",
    "researchGateUrl": "https://www.researchgate.net/publication/415534331_Quantifying_electrical_network_interconnectivity_in_polymer_nanocomposites_using_graph_theory"
  },
  {
    "id": "castillo-003",
    "title": "A cost-per-data-transmission model for unplanned downtime in smart city LoRa-based Beehive systems",
    "type": "article",
    "date": "2026-06-01",
    "venue": "Sustainable Energy Technologies and Assessments",
    "authors": "Alejandro Castillo Atoche, Orlando Palma Marrufo, Jaime Silverio Ortegón-Aguilar, [...], Johan Jair Estrada-López",
    "doi": "https://doi.org/10.1016/j.seta.2026.105121",
    "tags": [
      "wireless",
      "iot"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/407033739_A_cost-per-data-transmission_model_for_unplanned_downtime_in_smart_city_LoRa-based_Beehive_systems"
  },
  {
    "id": "castillo-004",
    "title": "Constrained Gray-Box Identification of Electromechanical Systems Under Unfiltered Step-Response Data",
    "type": "article",
    "date": "2025-12-01",
    "venue": "Information",
    "authors": "Carlos Fuentes-Silva, Omar Rodríguez-Abreo, Jesús Manuel Lugo-Quintal, [...], Enrique Camacho-Perez",
    "doi": "https://doi.org/10.3390/info16121079",
    "tags": [
      "sensors",
      "materials",
      "control_systems"
    ],
    "abstractKey": "abstract_castillo_004",
    "researchGateUrl": "https://www.researchgate.net/publication/398389147_Constrained_Gray-Box_Identification_of_Electromechanical_Systems_Under_Unfiltered_Step-Response_Data",
    "pdfUrl": "https://www.researchgate.net/journal/Information-2078-2489/publication/398389147_Constrained_Gray-Box_Identification_of_Electromechanical_Systems_Under_Unfiltered_Step-Response_Data/links/694cacd30c98040d4821a83c/Constrained-Gray-Box-Identification-of-Electromechanical-Systems-Under-Unfiltered-Step-Response-Data.pdf"
  },
  {
    "id": "castillo-005",
    "title": "Sustainable GIoT-Based Mangrove Monitoring System for Smart Coastal Cities with Energy Harvesting from SMFCs",
    "type": "article",
    "date": "2025-11-01",
    "venue": "Technologies",
    "authors": "Andrea Castillo-Atoche, Colin Garcia, Ramón Atoche-Enseñat, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.3390/technologies13120549",
    "pdfUrl": "https://www.mdpi.com/2227-7080/13/12/549/pdf?version=1764081152",
    "tags": [
      "iot",
      "energy_harvesting",
      "bioenergy",
      "environmental"
    ],
    "abstractKey": "abstract_castillo_005",
    "researchGateUrl": "https://www.researchgate.net/publication/397958839_Sustainable_GIoT-Based_Mangrove_Monitoring_System_for_Smart_Coastal_Cities_with_Energy_Harvesting_from_SMFCs"
  },
  {
    "id": "castillo-006",
    "title": "Toward Smart LoRa Network Management Through System Identification and Digital Twins: Mérida Case Study",
    "type": "conference-paper",
    "date": "2025-10-01",
    "venue": "IEEE CHILECON 2025",
    "authors": "Yumil Rueda Flores Trejo, Julio Heredia Lozano, Renan Quijano Cetina, [...], I. Lopez-Juarez",
    "doi": "https://doi.org/10.1109/chilecon66915.2025.11475994",
    "tags": [
      "wireless",
      "control_systems",
      "digital_twins"
    ],
    "abstractKey": "abstract_castillo_006",
    "researchGateUrl": "https://www.researchgate.net/publication/404013453_Toward_Smart_LoRa_Network_Management_Through_System_Identification_and_Digital_Twins_Merida_Case_Study"
  },
  {
    "id": "castillo-007",
    "title": "Electrical monitoring of structural health of multiscale hierarchical composites using fibers modified by graphenic sheets or carbon nanotubes",
    "type": "article",
    "date": "2025-08-01",
    "venue": "Composites Communications",
    "authors": "G. Uribe-Riestra, Julio Cesar Heredia-Lozano, M. Rivero-Ayala, [...], Francis Aviles",
    "doi": "https://doi.org/10.1016/j.coco.2025.102553",
    "tags": [
      "nanocomposites",
      "materials"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/394349725_Electrical_monitoring_of_structural_health_of_multiscale_hierarchical_composites_using_fibers_modified_by_graphenic_sheets_or_carbon_nanotubes"
  },
  {
    "id": "castillo-008",
    "title": "Integrated Dynamic Power Management Strategy with a Field Programmable Gate Array-Based Cryptoprocessor System for Secured Internet-of-Medical Things Networks",
    "type": "article",
    "date": "2025-02-01",
    "venue": "Technologies",
    "authors": "J. Vázquez Castillo, Daniel Visairo, Ramón Atoche-Enseñat, [...], Johan Jair Estrada-López",
    "doi": "https://doi.org/10.3390/technologies13020068",
    "pdfUrl": "https://www.mdpi.com/2227-7080/13/2/68/pdf?version=1738658259",
    "tags": [
      "fpga",
      "hardware",
      "power_electronics",
      "control_systems"
    ],
    "abstractKey": "abstract_castillo_008",
    "researchGateUrl": "https://www.researchgate.net/publication/388716674_Integrated_Dynamic_Power_Management_Strategy_with_a_Field_Programmable_Gate_Array-Based_Cryptoprocessor_System_for_Secured_Internet-of-Medical_Things_Networks"
  },
  {
    "id": "castillo-009",
    "title": "Low-Power System for Spatial Tactile Detection Using a Self-Sensing Nanocomposite With On-Device Adaptive Regularization",
    "type": "article",
    "date": "2025-01-01",
    "venue": "IEEE Transactions on Instrumentation and Measurement",
    "authors": "Julio Cesar Heredia-Lozano, Abraham Balam, Javier Vázquez-Castillo, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1109/tim.2025.3648090",
    "tags": [
      "nanocomposites",
      "sensors",
      "materials",
      "power_electronics"
    ],
    "abstractKey": "abstract_castillo_009",
    "researchGateUrl": "https://www.researchgate.net/publication/399057114_Low-Power_System_for_Spatial_Tactile_Detection_using_a_Self-Sensing_Nanocomposite_with_On-Device_Adaptive_Regularization"
  },
  {
    "id": "castillo-010",
    "title": "A Survey of Efficient Lightweight Cryptography for Power-Constrained Microcontrollers",
    "type": "article",
    "date": "2024-12-01",
    "venue": "Technologies",
    "authors": "Jesús Alejandro Soto Cruz, Erica Ruiz-Ibarra, J. Vázquez Castillo, [...], Joaquin Mass-Sanchez",
    "doi": "https://doi.org/10.3390/technologies13010003",
    "pdfUrl": "https://www.mdpi.com/2227-7080/13/1/3/pdf?version=1735052278",
    "tags": [
      "sensors",
      "control_systems",
      "cryptography"
    ],
    "abstractKey": "abstract_castillo_010",
    "researchGateUrl": "https://www.researchgate.net/publication/387417662_A_Survey_of_Efficient_Lightweight_Cryptography_for_Power-Constrained_Microcontrollers"
  },
  {
    "id": "castillo-011",
    "title": "Geo-Sensing-Based Analysis of Urban Heat Island in the Metropolitan Area of Merida, Mexico",
    "type": "article",
    "date": "2024-09-01",
    "venue": "Sensors",
    "authors": "Francisco A. Sánchez-Sánchez, Marisela I. Vega De Lille, Alejandro Castillo Atoche, [...], Juvenal Rodríguez Reséndiz",
    "doi": "https://doi.org/10.3390/s24196289",
    "tags": [
      "sensors",
      "environmental"
    ],
    "abstractKey": "abstract_castillo_011",
    "researchGateUrl": "https://www.researchgate.net/publication/384427405_Geo-Sensing-Based_Analysis_of_Urban_Heat_Island_in_the_Metropolitan_Area_of_Merida_Mexico"
  },
  {
    "id": "castillo-012",
    "title": "A Novel FPGA-Driven HD-SPWM Architecture With Zero-Sequence Voltage Insertion Strategy for Three-Level NPC Inverter",
    "type": "article",
    "date": "2024-09-01",
    "venue": "IEEE Transactions on Industrial Informatics",
    "authors": "Rishiraj Sarker, Avik Bhattacharya, Sudipta Debnath, [...], Asim Datta",
    "doi": "https://doi.org/10.1109/tii.2024.3393566",
    "tags": [
      "fpga",
      "power_electronics"
    ],
    "abstractKey": "abstract_castillo_012",
    "researchGateUrl": "https://www.researchgate.net/publication/380657476_A_Novel_FPGA-Driven_HD-SPWM_Architecture_With_Zero-Sequence_Voltage_Insertion_Strategy_for_Three-Level_NPC_Inverter",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-013",
    "title": "MediaPipe Frame and Convolutional Neural Networks-Based Fingerspelling Detection in Mexican Sign Language",
    "type": "article",
    "date": "2024-08-01",
    "venue": "Technologies",
    "authors": "Tzeico J. Sánchez-Vicinaiz, Enrique Camacho-Perez, Alejandro Castillo Atoche, [...], Juvenal Rodríguez Reséndiz",
    "doi": "https://doi.org/10.3390/technologies12080124",
    "tags": [
      "machine_learning",
      "biomedical"
    ],
    "abstractKey": "abstract_castillo_013",
    "researchGateUrl": "https://www.researchgate.net/publication/382795189_MediaPipe_Frame_and_Convolutional_Neural_Networks-Based_Fingerspelling_Detection_in_Mexican_Sign_Language"
  },
  {
    "id": "castillo-014",
    "title": "Sustainable urban noise monitoring network with renewable bioenergy sources: An energy cost–benefit analysis",
    "type": "article",
    "date": "2024-05-01",
    "venue": "Sustainable Cities and Society",
    "authors": "Alejandro Castillo Atoche, Naemi Maile, Johan Jair Estrada-López, [...], J. Vázquez Castillo",
    "doi": "https://doi.org/10.1016/j.scs.2024.105526",
    "tags": [
      "bioenergy",
      "renewable_energy",
      "environmental"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/380833014_Sustainable_urban_noise_monitoring_network_with_renewable_bioenergy_sources_An_energy_cost-benefit_analysis"
  },
  {
    "id": "castillo-015",
    "title": "Adhesion Testing System Based on Convolutional Neural Networks for Quality Inspection of Flexible Strain Sensors",
    "type": "article",
    "date": "2024-04-01",
    "venue": "IEEE Transactions on Industrial Informatics",
    "authors": "Ignacio Isaac Medina, Gabriel Arana, Andrea Cecilia Castillo Atoche, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1109/tii.2024.3383547",
    "tags": [
      "sensors",
      "materials",
      "machine_learning"
    ],
    "abstractKey": "abstract_castillo_015",
    "researchGateUrl": "https://www.researchgate.net/publication/379871912_Adhesion_Testing_System_Based_on_Convolutional_Neural_Networks_for_Quality_Inspection_of_Flexible_Strain_Sensors"
  },
  {
    "id": "castillo-016",
    "title": "Self-Tuning Model for Energy-Context Optimization in Perpetual Sensor Nodes Within IoT-Integrated Hydroponic Systems",
    "type": "article",
    "date": "2024-04-01",
    "venue": "IEEE Embedded Systems Letters",
    "authors": "Jose Agustin Hernandez Benitez, J. Vázquez Castillo, Johan Jair Estrada-López, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1109/les.2024.3387310",
    "tags": [
      "sensors",
      "wireless",
      "iot",
      "optimization"
    ],
    "abstractKey": "abstract_castillo_016",
    "researchGateUrl": "https://www.researchgate.net/publication/379871957_Self-Tuning_Model_for_Energy-Context_Optimization_in_Perpetual_Sensor_Nodes_Within_IoT-Integrated_Hydroponic_Systems"
  },
  {
    "id": "castillo-017",
    "title": "Plant microbial fuel cells as a bioenergy source used in precision beekeeping",
    "type": "article",
    "date": "2023-10-01",
    "venue": "Sustainable Energy Technologies and Assessments",
    "authors": "Edith Osorio, Mirna Valdez-Hernández, J. Vázquez Castillo, [...], Roberto la Rosa",
    "doi": "https://doi.org/10.1016/j.seta.2023.103499",
    "tags": [
      "bioenergy"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/374583642_Plant_microbial_fuel_cells_as_a_bioenergy_source_used_in_precision_beekeeping"
  },
  {
    "id": "castillo-018",
    "title": "A Study on the Renewable Power Generation Capacity of Microalgae Microbial Fuel Cells for Powering Giot Sensor Nodes",
    "type": "article",
    "date": "2023-10-01",
    "venue": "Journal of Power Sources",
    "authors": "Andrea Castillo-Atoche, Colin Garcia, Johan Jair Estrada-Lopez, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1016/j.jpowsour.2023.233185",
    "tags": [
      "sensors",
      "wireless",
      "iot",
      "bioenergy"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/367640352_A_Study_on_the_Renewable_Power_Generation_Capacity_of_Microalgae_Microbial_Fuel_Cells_for_Powering_Giot_Sensor_Nodes"
  },
  {
    "id": "castillo-019",
    "title": "Effect of temperature on the electrical and electromechanical properties of carbon nanotube/polypropylene composites",
    "type": "article",
    "date": "2023-06-01",
    "venue": "Smart Materials and Structures",
    "authors": "Abraham Balam, Z Valdez-Nava, Vincent Bley, [...], Francis Aviles",
    "doi": "https://doi.org/10.1088/1361-665x/acdf9f",
    "tags": [
      "nanocomposites",
      "materials"
    ],
    "abstractKey": "abstract_castillo_019",
    "researchGateUrl": "https://www.researchgate.net/publication/371720413_Effect_of_temperature_on_the_electrical_and_electromechanical_properties_of_carbon_nanotubepolypropylene_composites"
  },
  {
    "id": "castillo-020",
    "title": "Multimodal Power Management Based on Decision Tree for Internet of Wearable Things Systems",
    "type": "article",
    "date": "2023-03-01",
    "venue": "Applied Sciences",
    "authors": "Jaime Ortegón-Aguilar, Alejandro Castillo Atoche, Guillermo Becerra-Nunez, [...], J. Vázquez Castillo",
    "doi": "https://doi.org/10.3390/app13074351",
    "pdfUrl": "https://www.mdpi.com/2076-3417/13/7/4351/pdf?version=1680093809",
    "tags": [
      "iot",
      "power_electronics",
      "machine_learning",
      "biomedical"
    ],
    "abstractKey": "abstract_castillo_020",
    "researchGateUrl": "https://www.researchgate.net/publication/369662291_Multimodal_Power_Management_Based_on_Decision_Tree_for_Internet_of_Wearable_Things_Systems"
  },
  {
    "id": "castillo-021",
    "title": "A Sustainable Forage-Grass-Power Fuel Cell Solution for Edge-Computing Wireless Sensing Processing in Agriculture 4.0 Applications",
    "type": "article",
    "date": "2023-03-01",
    "venue": "Energies",
    "authors": "Johan Jair Estrada-López, J. Vázquez Castillo, Andrea Castillo-Atoche, [...], Alejandro Castillo Atoche",
    "tags": [
      "sensors",
      "wireless",
      "environmental"
    ],
    "abstractKey": "abstract_castillo_021",
    "researchGateUrl": "https://www.researchgate.net/publication/369541005_A_Sustainable_Forage-Grass-Power_Fuel_Cell_Solution_for_Edge-Computing_Wireless_Sensing_Processing_in_Agriculture_40_Applications",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-022",
    "title": "Plug-in Electric Vehicle Aided Load Frequency Control using Cascaded Controller in Microgrid System",
    "type": "conference-paper",
    "date": "2023-03-01",
    "venue": "IEEE GlobConHT 2023",
    "authors": "Indrajit Koley, Goutam Kumar Panda, Sanjoy Debbarma, [...], Asim Datta",
    "doi": "https://doi.org/10.1109/globconht56829.2023.10087793",
    "tags": [
      "microgrids",
      "power_electronics",
      "control_systems"
    ],
    "abstractKey": "abstract_castillo_022",
    "researchGateUrl": "https://www.researchgate.net/publication/369897794_Plug-in_Electric_Vehicle_Aided_Load_Frequency_Control_using_Cascaded_Controller_in_Microgrid_System"
  },
  {
    "id": "castillo-023",
    "title": "A Smart Tactile Sensing System Based on Carbon Nanotube/Polypropylene Composites for Wearable Applications",
    "type": "article",
    "date": "2023-02-01",
    "venue": "IEEE Sensors Journal",
    "authors": "J.R. Atoche Ensenat, E. Perez, Jose Agustin Hernandez Benitez, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1109/jsen.2022.3229232",
    "tags": [
      "nanocomposites",
      "sensors",
      "materials",
      "biomedical"
    ],
    "abstractKey": "abstract_castillo_023",
    "researchGateUrl": "https://www.researchgate.net/publication/366429670_A_Smart_Tactile_Sensing_System_Based_on_Carbon_NanotubePolypropylene_Composites_for_Wearable_Applications"
  },
  {
    "id": "castillo-024",
    "title": "Improving adhesion between polyimide surface and carbon nanotube arrays for strain-sensing devices",
    "type": "article",
    "date": "2022-11-01",
    "venue": "Materials Today Communications",
    "authors": "G. Arana, Maria Ortencia Gonzalez-Díaz, Alejandro Castillo Atoche, Francis Aviles",
    "doi": "https://doi.org/10.1016/j.mtcomm.2022.105008",
    "tags": [
      "nanocomposites",
      "sensors",
      "materials"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/365722356_Improving_adhesion_between_polyimide_surface_and_carbon_nanotube_arrays_for_strain-sensing_devices"
  },
  {
    "id": "castillo-025",
    "title": "Design and Implementation of an Interactive Photoplethysmography and Galvanic Skin Response Based Gamepad",
    "type": "chapter",
    "date": "2022-10-01",
    "venue": "Communications in Computer and Information Science",
    "authors": "Alejandro Sánchez-Sánchez, Mauricio Gabriel Orozco-del-Castillo, Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1007/978-3-031-18082-8_15",
    "tags": [
      "sensors",
      "robotics",
      "biomedical"
    ],
    "abstractKey": "abstract_castillo_025",
    "researchGateUrl": "https://www.researchgate.net/publication/364936852_Design_and_Implementation_of_an_Interactive_Photoplethysmography_and_Galvanic_Skin_Response_Based_Gamepad"
  },
  {
    "id": "castillo-026",
    "title": "An Accurate UAV Ground Landing Station System Based on BLE-RSSI and Maximum Likelihood Target Position Estimation",
    "type": "article",
    "date": "2022-06-01",
    "venue": "Applied Sciences",
    "authors": "Jaime Aviles, Roberto Carrasco-Alvarez, J. Vázquez Castillo, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.3390/app12136618",
    "pdfUrl": "https://www.mdpi.com/2076-3417/12/13/6618/pdf?version=1656565220",
    "tags": [
      "wireless",
      "robotics",
      "optimization",
      "mathematical_modeling"
    ],
    "abstractKey": "abstract_castillo_026",
    "researchGateUrl": "https://www.researchgate.net/publication/361694250_An_Accurate_UAV_Ground_Landing_Station_System_Based_on_BLE-RSSI_and_Maximum_Likelihood_Target_Position_Estimation"
  },
  {
    "id": "castillo-027",
    "title": "Plant/soil-microbial fuel cell operation effects in the biological activity of bioelectrochemical systems",
    "type": "article",
    "date": "2022-06-01",
    "venue": "Energy Sources, Part A: Recovery, Utilization, and Environmental Effects",
    "authors": "Mirna Valdez-Hernández, Leandro Nicolas Acquaroli, J. Vázquez Castillo, [...], Edith Osorio",
    "doi": "https://doi.org/10.1080/15567036.2022.2059597",
    "tags": [
      "bioenergy",
      "environmental"
    ],
    "abstractKey": "abstract_castillo_027",
    "researchGateUrl": "https://www.researchgate.net/publication/359804481_Plantsoil-microbial_fuel_cell_operation_effects_in_the_biological_activity_of_bioelectrochemical_systems"
  },
  {
    "id": "castillo-028",
    "title": "An Ultra-Low-Power Strain Sensing Node for Long-Range Wireless Networks in Carbon Nanotube-Based Materials",
    "type": "article",
    "date": "2022-05-01",
    "venue": "IEEE Sensors Journal",
    "authors": "Jose Agustin Hernandez Benitez, Abraham Balam, J. Vázquez Castillo, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1109/jsen.2022.3162988",
    "tags": [
      "nanocomposites",
      "sensors",
      "materials",
      "wireless"
    ],
    "abstractKey": "abstract_castillo_028",
    "researchGateUrl": "https://www.researchgate.net/publication/359811906_An_ultra-low-power_strain_sensing_node_for_Long-Range_wireless_networks_in_carbon_nanotube-based_materials"
  },
  {
    "id": "castillo-029",
    "title": "Energy Efficient Framework for a AIoT Cardiac Arrhythmia Detection System Wearable during Sport",
    "type": "article",
    "date": "2022-03-01",
    "venue": "Applied Sciences",
    "authors": "Alejandro Castillo Atoche, Karim Caamal-Herrera, J.R. Atoche Ensenat, [...], Adolfo Espinoza-Ruiz",
    "doi": "https://doi.org/10.3390/app12052716",
    "pdfUrl": "https://www.mdpi.com/2076-3417/12/5/2716/pdf?version=1646479878",
    "tags": [
      "iot",
      "biomedical"
    ],
    "abstractKey": "abstract_castillo_029",
    "researchGateUrl": "https://www.researchgate.net/publication/359062645_Energy_Efficient_Framework_for_a_AIoT_Cardiac_Arrhythmia_Detection_System_Wearable_during_Sport"
  },
  {
    "id": "castillo-030",
    "title": "An Optimization Methodology for Designing Hardware-Based Function Evaluation Modules with Reduced Complexity",
    "type": "article",
    "date": "2022-03-01",
    "venue": "Circuits, Systems, and Signal Processing",
    "authors": "G. González-Díaz-Conti, Omar Longoria-Gandara, Roberto Carrasco-Alvarez, [...], J. Vázquez Castillo",
    "doi": "https://doi.org/10.1007/s00034-021-01835-1",
    "tags": [
      "hardware",
      "signal_processing",
      "optimization"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/355182753_An_Optimization_Methodology_for_Designing_Hardware-Based_Function_Evaluation_Modules_with_Reduced_Complexity",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-032",
    "title": "Energy-Saving Techniques for Urban Noise WSN With Kalman-Based State Estimation and Green Facade Energy Harvester",
    "type": "article",
    "date": "2022-01-01",
    "venue": "IEEE Transactions on Instrumentation and Measurement",
    "authors": "J. Vázquez Castillo, Alejandro Castillo Atoche, Johan Jair Estrada-López, [...], Victor Sandoval-Curmina",
    "doi": "https://doi.org/10.1109/tim.2022.3145385",
    "tags": [
      "wireless",
      "energy_harvesting",
      "control_systems",
      "environmental"
    ],
    "abstractKey": "abstract_castillo_032",
    "researchGateUrl": "https://www.researchgate.net/publication/358100185_Energy-Saving_Techniques_for_Urban_Noise_WSN_with_Kalman-Based_State_Estimation_and_Green_Facade_Energy_Harvester",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-033",
    "title": "Hardware-Based Activation Function-Core for Neural Network Implementations",
    "type": "article",
    "date": "2021-12-01",
    "venue": "Electronics",
    "authors": "Griselda González-Díaz_Conti, J. Vázquez Castillo, Omar Longoria-Gandara, [...], Erica Ruiz-Ibarra",
    "doi": "https://doi.org/10.3390/electronics11010014",
    "pdfUrl": "https://www.mdpi.com/2079-9292/11/1/14/pdf?version=1640228819",
    "tags": [
      "hardware",
      "machine_learning"
    ],
    "abstractKey": "abstract_castillo_033",
    "researchGateUrl": "https://www.researchgate.net/publication/357261094_Hardware-Based_Activation_Function-Core_for_Neural_Network_Implementations"
  },
  {
    "id": "castillo-034",
    "title": "Investigation of directional effects on the electrical conductivity and piezoresistivity of carbon nanotube/polypropylene composites obtained by extrusion",
    "type": "article",
    "date": "2021-09-01",
    "venue": "Journal of Materials Science",
    "authors": "Abraham Balam, Ricardo Herbé Cruz-Estrada, Alejandro Castillo Atoche, Francis Aviles",
    "doi": "https://doi.org/10.1007/s10853-021-06223-3",
    "tags": [
      "nanocomposites",
      "sensors",
      "materials"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/352507315_Investigation_of_directional_effects_on_the_electrical_conductivity_and_piezoresistivity_of_carbon_nanotubepolypropylene_composites_obtained_by_extrusion",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-035",
    "title": "Coordinated AC frequency versus DC voltage control in a PV-wind-battery based hybrid AC/DC microgrid",
    "type": "article",
    "date": "2021-08-01",
    "venue": "International Transactions on Electrical Energy Systems",
    "authors": "Asim Datta, Alejandro Castillo Atoche, Indrajit Koley, [...], Debasree Saha",
    "tags": [
      "renewable_energy",
      "microgrids",
      "power_electronics",
      "control_systems"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/353331078_Coordinated_AC_frequency_versus_DC_voltage_control_in_a_PV-wind-battery_based_hybrid_ACDC_microgrid",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-036",
    "title": "Dynamic Power–Frequency Control in a Hybrid Wind-PV Plant Interlinked with AC Power System",
    "type": "article",
    "date": "2021-03-01",
    "venue": "Journal of Electrical Engineering and Technology",
    "authors": "Asim Datta, Indrajit Koley, Goutam K. Panda, [...], J. Vázquez Castillo",
    "doi": "https://doi.org/10.1007/s42835-021-00691-y",
    "tags": [
      "renewable_energy",
      "power_electronics",
      "control_systems",
      "photovoltaics"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/349839155_Dynamic_Power-Frequency_Control_in_a_Hybrid_Wind-PV_Plant_Interlinked_with_AC_Power_System"
  },
  {
    "id": "castillo-037",
    "title": "An Energy-Saving Data Statistics-Driven Management Technique for Bio-Powered Indoor Wireless Sensor Nodes",
    "type": "article",
    "date": "2021-03-01",
    "venue": "IEEE Transactions on Instrumentation and Measurement",
    "authors": "Alejandro Castillo Atoche, J. Vázquez Castillo, Edith Osorio, [...], Johan Jair Estrada-López",
    "doi": "https://doi.org/10.1109/tim.2021.3063187",
    "tags": [
      "sensors",
      "wireless",
      "energy_harvesting",
      "mathematical_modeling"
    ],
    "abstractKey": "abstract_castillo_037",
    "researchGateUrl": "https://www.researchgate.net/publication/349756935_An_Energy-Saving_Data_Statistics-Driven_Management_Technique_for_Bio-Powered_Indoor_Wireless_Sensor_Nodes"
  },
  {
    "id": "castillo-038",
    "title": "Arrays of Plant Microbial Fuel Cells for Implementing Self-Sustainable Wireless Sensor Networks",
    "type": "article",
    "date": "2021-01-01",
    "venue": "IEEE Sensors Journal",
    "authors": "Edith Osorio, J. Vázquez Castillo, Alejandro Castillo Atoche, [...], Romeli Barbosa",
    "doi": "https://doi.org/10.1109/jsen.2020.3019986",
    "tags": [
      "sensors",
      "wireless",
      "energy_harvesting",
      "bioenergy"
    ],
    "abstractKey": "abstract_castillo_038",
    "researchGateUrl": "https://www.researchgate.net/publication/343952234_Arrays_of_Plant_Microbial_Fuel_Cells_for_Implementing_Self-Sustainable_Wireless_Sensor_Networks"
  },
  {
    "id": "castillo-040",
    "title": "Influence of electrode configuration on impact damage evaluation of self-sensing hierarchical composites",
    "type": "article",
    "date": "2020-06-01",
    "venue": "Journal of Intelligent Material Systems and Structures",
    "authors": "Gabriela Carolina Uribe-Riestra, Jorge Andrés Ocampo-Bello, Fidel Gamboa, [...], Francis Aviles",
    "doi": "https://doi.org/10.1177/1045389x20919979",
    "tags": [
      "sensors",
      "materials"
    ],
    "abstractKey": "abstract_castillo_040",
    "researchGateUrl": "https://www.researchgate.net/publication/341843264_Influence_of_electrode_configuration_on_impact_damage_evaluation_of_self-sensing_hierarchical_composites",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-041",
    "title": "FPGA-Based Hardware Matrix Inversion Architecture Using Hybrid Piecewise Polynomial Approximation Systolic Cells",
    "type": "article",
    "date": "2020-01-01",
    "venue": "Electronics",
    "authors": "J. Vázquez Castillo, Alejandro Castillo Atoche, Roberto Carrasco-Alvarez, [...], Jaime Ortegón-Aguilar",
    "doi": "https://doi.org/10.3390/electronics9010182",
    "pdfUrl": "https://www.mdpi.com/2079-9292/9/1/182/pdf?version=1579326047",
    "tags": [
      "fpga",
      "hardware"
    ],
    "abstractKey": "abstract_castillo_041",
    "researchGateUrl": "https://www.researchgate.net/publication/338692860_FPGA-Based_Hardware_Matrix_Inversion_Architecture_Using_Hybrid_Piecewise_Polynomial_Approximation_Systolic_Cells"
  },
  {
    "id": "castillo-042",
    "title": "Intelligent Classification of Large-Scale Remotely Sensed Hyperspectral Images using Multi-GPU Computing",
    "type": "article",
    "date": "2020-01-01",
    "venue": "IEEE Latin America Transactions",
    "authors": "Alejandro Castillo Atoche, J. Vázquez Castillo, Jaime Ortegon Aguilar, [...], Jaime Aviles",
    "doi": "https://doi.org/10.1109/tla.2020.9049468",
    "tags": [
      "machine_learning",
      "remote_sensing",
      "image_processing",
      "gpu_computing"
    ],
    "abstractKey": "abstract_castillo_042",
    "researchGateUrl": "https://www.researchgate.net/publication/340241911_Intelligent_Classification_of_Large-Scale_Remotely_Sensed_Hyperspectral_Images_using_Multi-GPU_Computing",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-043",
    "title": "Design and Fabrication of a 3-D Printed Concentrating Solar Thermoelectric Generator for Energy Harvesting Based Wireless Sensor Nodes",
    "type": "article",
    "date": "2019-10-01",
    "venue": "IEEE Sensors Letters",
    "authors": "Johan J. Estrada-Lopez, Alejandro Castillo Atoche, Edgar Sanchez-Sinencio",
    "doi": "https://doi.org/10.1109/lsens.2019.2948811",
    "tags": [
      "sensors",
      "wireless",
      "energy_harvesting",
      "renewable_energy"
    ],
    "abstractKey": "abstract_castillo_043",
    "researchGateUrl": "https://www.researchgate.net/publication/336757753_Design_and_Fabrication_of_a_3-D_Printed_Concentrating_Solar_Thermoelectric_Generator_for_Energy_Harvesting_Based_Wireless_Sensor_Nodes"
  },
  {
    "id": "castillo-044",
    "title": "A Self-Powered PMFC-Based Wireless Sensor Node for Smart City Applications",
    "type": "article",
    "date": "2019-06-01",
    "venue": "Wireless Communications and Mobile Computing",
    "authors": "Daniel Ayala-Ruiz, Alejandro Castillo Atoche, Erica Ruiz-Ibarra, [...], J. Vázquez Castillo",
    "doi": "https://doi.org/10.1155/2019/8986302",
    "pdfUrl": "https://downloads.hindawi.com/journals/wcmc/2019/8986302.pdf",
    "tags": [
      "sensors",
      "wireless",
      "iot",
      "energy_harvesting"
    ],
    "abstractKey": "abstract_castillo_044",
    "researchGateUrl": "https://www.researchgate.net/publication/333591074_A_Self-Powered_PMFC-Based_Wireless_Sensor_Node_for_Smart_City_Applications"
  },
  {
    "id": "castillo-045",
    "title": "Plant Microbial Fuel Cells–Based Energy Harvester System for Self-powered IoT Applications",
    "type": "article",
    "date": "2019-03-01",
    "venue": "Sensors",
    "authors": "Edith Osorio, J. Vázquez Castillo, Mario Alberto Carmona Campos, [...], Jaime Ortegón Aguilar",
    "doi": "https://doi.org/10.3390/s19061378",
    "pdfUrl": "https://www.mdpi.com/1424-8220/19/6/1378/pdf?version=1553062668",
    "tags": [
      "iot",
      "energy_harvesting",
      "bioenergy"
    ],
    "abstractKey": "abstract_castillo_045",
    "researchGateUrl": "https://www.researchgate.net/publication/331930676_Plant_Microbial_Fuel_Cells-Based_Energy_Harvester_System_for_Self-powered_IoT_Applications"
  },
  {
    "id": "castillo-046",
    "title": "Smart Soil Parameters Estimation System Using an Autonomous Wireless Sensor Network With Dynamic Power Management Strategy",
    "type": "article",
    "date": "2018-11-01",
    "venue": "IEEE Sensors Journal",
    "authors": "Johan Jair Estrada-López, Alejandro Castillo Atoche, J. Vázquez Castillo, E. Sanchez-Sinencio",
    "doi": "https://doi.org/10.1109/jsen.2018.2867432",
    "tags": [
      "sensors",
      "wireless",
      "power_electronics",
      "control_systems"
    ],
    "abstractKey": "abstract_castillo_046",
    "researchGateUrl": "https://www.researchgate.net/publication/327298273_Smart_Soil_Parameters_Estimation_System_Using_an_Autonomous_Wireless_Sensor_Network_With_Dynamic_Power_Management_Strategy",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-047",
    "title": "Hardware design of computer arithmetic blocks for engineering laboratory practices",
    "type": "article",
    "date": "2018-08-01",
    "venue": "IEEE Latin America Transactions",
    "authors": "Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1109/tla.2018.8444156",
    "tags": [
      "hardware",
      "education"
    ],
    "abstractKey": "abstract_castillo_047",
    "researchGateUrl": "https://www.researchgate.net/publication/327142199_Hardware_design_of_computer_arithmetic_blocks_for_engineering_laboratory_practices"
  },
  {
    "id": "castillo-048",
    "title": "Adaptive segmentation methodology for hardware function evaluators",
    "type": "article",
    "date": "2018-07-01",
    "venue": "Computers & Electrical Engineering",
    "authors": "Juan Martin Trejo-Arellano, J. Vázquez Castillo, Omar Longoria-Gandara, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1016/j.compeleceng.2018.04.024",
    "tags": [
      "hardware",
      "signal_processing"
    ],
    "abstractKey": "abstract_castillo_048",
    "researchGateUrl": "https://www.researchgate.net/publication/325782757_Adaptive_segmentation_methodology_for_hardware_function_evaluators"
  },
  {
    "id": "castillo-050",
    "title": "Low-cost power systems metrology laboratory based on raspberry Pi",
    "type": "conference-paper",
    "date": "2018-04-01",
    "venue": "SMAGRIMET 2018",
    "authors": "Renan Quijano Cetina, A. J. Roscoe, Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.23919/smagrimet.2018.8369843",
    "tags": [
      "power_electronics",
      "education",
      "mathematical_modeling"
    ],
    "abstractKey": "abstract_castillo_050",
    "researchGateUrl": "https://www.researchgate.net/publication/325493355_Low-cost_power_systems_metrology_laboratory_based_on_raspberry_Pi",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-051",
    "title": "A triply selective MIMO channel simulator using GPUs",
    "type": "article",
    "date": "2018-03-01",
    "venue": "Wireless Communications and Mobile Computing",
    "authors": "Roberto Carrasco-Alvarez, R. Carreón-Villal, J. Vázquez Castillo, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1155/2018/3517489",
    "pdfUrl": "https://downloads.hindawi.com/journals/wcmc/2018/3517489.pdf",
    "tags": [
      "communications",
      "channel_emulation"
    ],
    "abstractKey": "abstract_castillo_051",
    "researchGateUrl": "https://www.researchgate.net/publication/323581615_A_triply_selective_MIMO_channel_simulator_using_GPUs"
  },
  {
    "id": "castillo-052",
    "title": "Material phase classification by means of Support Vector Machines",
    "type": "article",
    "date": "2017-12-01",
    "venue": "Computational Materials Science",
    "authors": "Jaime Ortegon, René Ledesma-Alonso, Romeli Barbosa, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1016/j.commatsci.2018.02.054",
    "pdfUrl": "https://arxiv.org/pdf/1712.04550",
    "pdfPath": "/pdf/publications/castillo_052.pdf",
    "tags": [
      "materials",
      "machine_learning",
      "mathematical_modeling"
    ],
    "abstractKey": "abstract_castillo_052",
    "researchGateUrl": "https://www.researchgate.net/publication/321794393_Material_phase_classification_by_means_of_Support_Vector_Machines"
  },
  {
    "id": "castillo-053",
    "title": "Material phase classification by means of Support Vector Machines",
    "type": "preprint",
    "date": "2017-12-01",
    "venue": "arXiv",
    "authors": "Jaime Ortegon, René Ledesma-Alonso, Romeli Barbosa, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1016/j.commatsci.2018.02.054",
    "pdfUrl": "https://arxiv.org/pdf/1712.04550",
    "pdfPath": "/pdf/publications/castillo_052.pdf",
    "tags": [
      "materials",
      "machine_learning",
      "mathematical_modeling"
    ],
    "abstractKey": "abstract_castillo_053",
    "researchGateUrl": "https://www.researchgate.net/publication/386837684_Material_phase_classification_by_means_of_Support_Vector_Machines"
  },
  {
    "id": "castillo-054",
    "title": "A novel function segmentation methodology for implementing affordable channel emulators",
    "type": "conference-paper",
    "date": "2016-12-01",
    "venue": "IEEE LAMC 2016",
    "authors": "Juan Martin Trejo-Arellano, J. Vázquez Castillo, Omar Longoria-Gandara, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1109/lamc.2016.7851264",
    "tags": [
      "communications",
      "signal_processing",
      "channel_emulation"
    ],
    "abstractKey": "abstract_castillo_054",
    "researchGateUrl": "https://www.researchgate.net/publication/313805366_A_novel_function_segmentation_methodology_for_implementing_affordable_channel_emulators"
  },
  {
    "id": "castillo-055",
    "title": "Enhancement and Edge-Preserving Denoising: An OpenCL-Based Approach for Remote Sensing Imagery",
    "type": "article",
    "date": "2016-11-01",
    "venue": "IEEE Journal of Selected Topics in Applied Earth Observations and Remote Sensing",
    "authors": "Jaime Ortegon Aguilar, Alejandro Castillo Atoche, Roberto Carrasco Alvarez, [...], Omar Perez-Martinez",
    "doi": "https://doi.org/10.1109/jstars.2016.2617292",
    "pdfUrl": "https://rei.iteso.mx/bitstreams/0701c02f-6f72-4852-af65-16a63743eeed/download",
    "pdfPath": "/pdf/publications/castillo_055.pdf",
    "tags": [
      "sensors",
      "remote_sensing",
      "image_processing",
      "gpu_computing"
    ],
    "abstractKey": "abstract_castillo_055",
    "researchGateUrl": "https://www.researchgate.net/publication/309818374_Enhancement_and_Edge-Preserving_Denoising_An_OpenCL-Based_Approach_for_Remote_Sensing_Imagery"
  },
  {
    "id": "castillo-056",
    "title": "An improved hardware design for matrix inverse based on systolic array QR decomposition and piecewise polynomial approximation",
    "type": "conference-paper",
    "date": "2015-12-01",
    "venue": "ReConFig 2015",
    "authors": "Lucio Canche, Alejandro Castillo Atoche, J. Vázquez Castillo, [...], J. Ortegon Aguilar",
    "doi": "https://doi.org/10.1109/reconfig.2015.7393290",
    "tags": [
      "hardware",
      "signal_processing"
    ],
    "abstractKey": "abstract_castillo_056",
    "researchGateUrl": "https://www.researchgate.net/publication/304297546_An_improved_hardware_design_for_matrix_inverse_based_on_systolic_array_QR_decomposition_and_piecewise_polynomial_approximation"
  },
  {
    "id": "castillo-057",
    "title": "A high-accuracy photovoltaic emulator system using ARM processors",
    "type": "article",
    "date": "2015-10-01",
    "venue": "Solar Energy",
    "authors": "Alejandro Castillo Atoche, J. Vázquez Castillo, J. Ortegón Aguilar, [...], Adrian Colli-Menchi",
    "doi": "https://doi.org/10.1016/j.solener.2015.05.026",
    "tags": [
      "renewable_energy",
      "photovoltaics"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/281226314_A_high-accuracy_photovoltaic_emulator_system_using_ARM_processors"
  },
  {
    "id": "castillo-058",
    "title": "A case study of OpenCL-based parallel programming for low-power remote sensing applications",
    "type": "conference-paper",
    "date": "2015-10-01",
    "venue": "ICEEE 2015",
    "authors": "A. Castro Angulo, Roberto Carrasco-Alvarez, J. Ortegon Aguilar, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1109/iceee.2015.7357959",
    "tags": [
      "sensors",
      "remote_sensing",
      "gpu_computing"
    ],
    "abstractKey": "abstract_castillo_058",
    "researchGateUrl": "https://www.researchgate.net/publication/308808456_A_case_study_of_OpenCL-based_parallel_programming_for_low-power_remote_sensing_applications"
  },
  {
    "id": "castillo-059",
    "title": "Detección de defectos con y sin apoyo de un entorno virtual colaborativo inteligente en cursos introductorios de programación",
    "type": "chapter",
    "date": "2015-05-01",
    "authors": "Juan Pablo Ucán Pech, Omar S. Gómez, Alejandro Castillo Atoche, Raúl Antonio Aguilar Vera",
    "tags": [
      "education"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/277721620_Deteccion_de_defectos_con_y_sin_apoyo_de_un_entorno_virtual_colaborativo_inteligente_en_cursos_introductorios_de_programacion",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-060",
    "title": "A Fading Channel Simulator Implementation Based on GPU Computing Techniques",
    "type": "article",
    "date": "2015-04-01",
    "venue": "Mathematical Problems in Engineering",
    "authors": "Roberto Carrasco-Alvarez, J. Vázquez Castillo, Alejandro Castillo Atoche, J. Ortegón Aguilar",
    "doi": "https://doi.org/10.1155/2015/237061",
    "pdfUrl": "https://downloads.hindawi.com/journals/mpe/2015/237061.pdf",
    "tags": [
      "gpu_computing",
      "communications",
      "channel_emulation"
    ],
    "abstractKey": "abstract_castillo_060",
    "researchGateUrl": "https://www.researchgate.net/publication/275223879_A_Fading_Channel_Simulator_Implementation_Based_on_GPU_Computing_Techniques"
  },
  {
    "id": "castillo-061",
    "title": "Detection of defects with and without support of an intelligent collaborative virtual environment in introductory programming courses",
    "type": "article",
    "date": "2015-01-01",
    "authors": "Juan Pablo Ucán Pech, Omar S. Gómez, Alejandro Castillo Atoche, Raúl Antonio Aguilar Vera",
    "tags": [
      "environmental",
      "education"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/282202413_Detection_of_defects_with_and_without_support_of_an_intelligent_collaborative_virtual_environment_in_introductory_programming_courses"
  },
  {
    "id": "castillo-062",
    "title": "Hybrid FPGA/ARM Co-design for Near Real Time of Remote Sensing Imagery",
    "type": "chapter",
    "date": "2014-11-01",
    "venue": "Lecture Notes in Computer Science",
    "authors": "C. Góngora-Martín, Alejandro Castillo Atoche, Johan Jair Estrada-López, [...], Roberto Carrasco-Alvarez",
    "doi": "https://doi.org/10.1007/978-3-319-12568-8_126",
    "pdfUrl": "https://link.springer.com/content/pdf/10.1007%2F978-3-319-12568-8_126.pdf",
    "tags": [
      "sensors",
      "fpga",
      "remote_sensing",
      "image_processing"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/312735190_Hybrid_FPGAARM_Co-design_for_Near_Real_Time_of_Remote_Sensing_Imagery"
  },
  {
    "id": "castillo-063",
    "title": "An improved RBR image reconstruction architecture based on super-systolic techniques",
    "type": "article",
    "date": "2014-10-01",
    "venue": "Journal of Ambient Intelligence and Humanized Computing",
    "authors": "Alejandro Castillo Atoche, Renan Quijano Cetina, Orlando Palma Marrufo",
    "doi": "https://doi.org/10.1007/s12652-013-0202-y",
    "tags": [
      "hardware",
      "image_processing"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/257793026_An_improved_RBR_image_reconstruction_architecture_based_on_super-systolic_techniques"
  },
  {
    "id": "castillo-064",
    "title": "Virtual Laboratory for Digital Image Processing",
    "type": "article",
    "date": "2014-09-01",
    "venue": "IEEE Latin America Transactions",
    "authors": "Alejandro Castillo Atoche, Jaime Ortegon, J. Vázquez Castillo, Jorge Rivera",
    "doi": "https://doi.org/10.1109/tla.2014.6894017",
    "tags": [
      "image_processing",
      "education"
    ],
    "abstractKey": "abstract_castillo_064",
    "researchGateUrl": "https://www.researchgate.net/publication/265416584_Virtual_Laboratory_for_Digital_Image_Processing",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-065",
    "title": "An Embedded Hardware Emulator Laboratory of Solar Photovoltaic Modules",
    "type": "conference-paper",
    "date": "2014-08-01",
    "authors": "José Maria Castillo-Cruz, O. Zavala-Duran, Renan Quijano Cetina, [...], Alejandro Castillo Atoche",
    "tags": [
      "hardware",
      "renewable_energy",
      "education",
      "photovoltaics"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/269635754_An_Embedded_Hardware_Emulator_Laboratory_of_Solar_Photovoltaic_Modules"
  },
  {
    "id": "castillo-066",
    "title": "An efficient systolic array grid-based structure of the robust Bayesian regularization technique for real-time enhanced imaging in uncertain remote sensing environment",
    "type": "article",
    "date": "2014-07-01",
    "venue": "Journal of Real-Time Image Processing",
    "authors": "Alejandro Castillo Atoche, Roberto Carrasco-Alvarez, O. Palma Marrufo, J. Vázquez Castillo",
    "doi": "https://doi.org/10.1007/s11554-014-0441-y",
    "tags": [
      "sensors",
      "hardware",
      "machine_learning",
      "remote_sensing"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/271660241_An_efficient_systolic_array_grid-based_structure_of_the_robust_Bayesian_regularization_technique_for_real-time_enhanced_imaging_in_uncertain_remote_sensing_environment"
  },
  {
    "id": "castillo-067",
    "title": "Parallel multispectral remote sensing data analysis: a case study of biosphere analysis on Yucatan peninsula",
    "type": "conference-paper",
    "date": "2014-07-01",
    "authors": "Alejandro Castillo Atoche",
    "tags": [
      "sensors",
      "remote_sensing"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/269635919_Parallel_multispectral_remote_sensing_data_analysis_a_case_study_of_biosphere_analysis_on_Yucatan_peninsula"
  },
  {
    "id": "castillo-068",
    "title": "Solar Photovoltaic Emulator System Based on a Systolic Array Network",
    "type": "article",
    "date": "2014-05-01",
    "venue": "IEICE Transactions on Fundamentals of Electronics, Communications and Computer Sciences",
    "authors": "Pedro Perez Munoz, Renan Quijano Cetina, Manuel Flota-Bañuelos, Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1587/transfun.e97.a.1119",
    "tags": [
      "hardware",
      "renewable_energy",
      "photovoltaics"
    ],
    "abstractKey": "abstract_castillo_068",
    "researchGateUrl": "https://www.researchgate.net/publication/278180112_Solar_Photovoltaic_Emulator_System_Based_on_a_Systolic_Array_Network"
  },
  {
    "id": "castillo-069",
    "title": "Aggregation of Descriptive Regularization and Fuzzy Logic Techniques for Enhanced Remote Sensing Imaging",
    "type": "article",
    "date": "2014-01-01",
    "authors": "Alejandro Castillo Atoche, O. Palma Marrufo, Ricardo Javier Peón-Escalante",
    "doi": "https://doi.org/10.5220/0005154301930198",
    "tags": [
      "sensors",
      "machine_learning",
      "remote_sensing",
      "image_processing"
    ],
    "abstractKey": "abstract_castillo_069",
    "researchGateUrl": "https://www.researchgate.net/publication/288771099_Aggregation_of_Descriptive_Regularization_and_Fuzzy_Logic_Techniques_for_Enhanced_Remote_Sensing_Imaging"
  },
  {
    "id": "castillo-070",
    "title": "An Improved Performance for Real-Time Hyperspectral Image Classification using Multi-GPU Computing",
    "type": "conference-paper",
    "date": "2013-06-01",
    "authors": "F. Solis Lugo, Alejandro Castillo Atoche, Roberto Carrasco-Alvarez, J. Vázquez Castillo",
    "tags": [
      "remote_sensing",
      "image_processing",
      "gpu_computing",
      "mathematical_modeling"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/269635934_An_Improved_Performance_for_Real-Time_Hyperspectral_Image_Classification_using_Multi-GPU_Computing"
  },
  {
    "id": "castillo-071",
    "title": "A New Tool for Intelligent Parallel Processing of Radar/SAR Remotely Sensed Imagery",
    "type": "article",
    "date": "2013-01-01",
    "venue": "Mathematical Problems in Engineering",
    "authors": "Alejandro Castillo Atoche, Roberto Carrasco-Alvarez, J. Ortegón Aguilar, J. Vázquez Castillo",
    "doi": "https://doi.org/10.1155/2013/405372",
    "pdfUrl": "https://downloads.hindawi.com/journals/mpe/2013/405372.pdf",
    "tags": [
      "remote_sensing",
      "image_processing",
      "gpu_computing"
    ],
    "abstractKey": "abstract_castillo_071",
    "researchGateUrl": "https://www.researchgate.net/publication/258393170_A_New_Tool_for_Intelligent_Parallel_Processing_of_RadarSAR_Remotely_Sensed_Imagery"
  },
  {
    "id": "castillo-072",
    "title": "GPU Implementation of Fuzzy Anisotropic Diffusion",
    "type": "conference-paper",
    "date": "2012-06-01",
    "authors": "J. Duarte Coello, F.J. Solís Lugo, Alejandro Castillo Atoche, J. Ortegón Aguilar",
    "tags": [
      "machine_learning",
      "gpu_computing"
    ],
    "abstractKey": "abstract_castillo_072",
    "researchGateUrl": "https://www.researchgate.net/publication/269638574_GPU_Implementation_of_Fuzzy_Anisotropic_Diffusion"
  },
  {
    "id": "castillo-073",
    "title": "Dual Super-Systolic Core for Real-Time Reconstructive Algorithms of High-Resolution Radar/SAR Imaging Systems",
    "type": "article",
    "date": "2012-02-01",
    "venue": "Sensors",
    "authors": "Alejandro Castillo Atoche, J. Vázquez Castillo",
    "doi": "https://doi.org/10.3390/s120302539",
    "pdfUrl": "https://www.mdpi.com/1424-8220/12/3/2539/pdf?version=1403317167",
    "tags": [
      "hardware",
      "remote_sensing",
      "image_processing"
    ],
    "abstractKey": "abstract_castillo_073",
    "researchGateUrl": "https://www.researchgate.net/publication/228071774_Dual_Super-Systolic_Core_for_Real-Time_Reconstructive_Algorithms_of_High-Resolution_RadarSAR_Imaging_Systems"
  },
  {
    "id": "castillo-074",
    "title": "High-speed low-power parallel Random Number Generator for wireless channel emulators",
    "type": "article",
    "date": "2012-02-01",
    "venue": "IEEE LASCAS 2012",
    "authors": "J. Vázquez Castillo, Luis Vela-Garcia, Ramon Parra-Michel, [...], Johan Jair Estrada-López",
    "doi": "https://doi.org/10.1109/lascas.2012.6180346",
    "tags": [
      "wireless",
      "communications",
      "channel_emulation"
    ],
    "abstractKey": "abstract_castillo_074",
    "researchGateUrl": "https://www.researchgate.net/publication/241628839_High-speed_low-power_parallel_Random_Number_Generator_for_wireless_channel_emulators",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-075",
    "title": "Efficient design of bit-level accelerator architectures for the DEDR-RASF remote sensing algorithm using super-systolic arrays",
    "type": "article",
    "date": "2012-01-01",
    "authors": "Alejandro Castillo Atoche, Johan Jair Estrada-López, Renan Quijano Cetina, Luis Rizo",
    "doi": "https://doi.org/10.5220/0003835203270333",
    "tags": [
      "sensors",
      "hardware",
      "remote_sensing"
    ],
    "abstractKey": "abstract_castillo_075",
    "researchGateUrl": "https://www.researchgate.net/publication/288659719_Efficient_design_of_bit-level_accelerator_architectures_for_the_DEDR-RASF_remote_sensing_algorithm_using_super-systolic_arrays"
  },
  {
    "id": "castillo-076",
    "title": "Near real time enhancement of geospatial imagery via systolic implementation of neural network-adapted convex regularization techniques",
    "type": "article",
    "date": "2011-12-01",
    "venue": "Pattern Recognition Letters",
    "authors": "Shkvarko Yuriy, Alejandro Castillo Atoche, Deni Torres-Roman",
    "doi": "https://doi.org/10.1016/j.patrec.2011.05.018",
    "tags": [
      "hardware",
      "machine_learning",
      "remote_sensing",
      "image_processing"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/220643970_Near_real_time_enhancement_of_geospatial_imagery_via_systolic_implementation_of_neural_network-adapted_convex_regularization_techniques"
  },
  {
    "id": "castillo-077",
    "title": "High-Speed VLSI Architecture Based on Massively Parallel Processor Arrays for Real-Time Remote Sensing Applications",
    "type": "chapter",
    "date": "2011-11-01",
    "venue": "IntechOpen",
    "authors": "Alejandro Castillo Atoche, Johan Jair Estrada-López, P. Perez Muñoz, Sergio Soto-Aguilar",
    "doi": "https://doi.org/10.5772/26496",
    "pdfUrl": "https://www.intechopen.com/citation-pdf-url/24306",
    "pdfPath": "/pdf/publications/castillo_077.pdf",
    "tags": [
      "sensors",
      "fpga",
      "hardware",
      "remote_sensing"
    ],
    "abstractKey": "abstract_castillo_077",
    "researchGateUrl": "https://www.researchgate.net/publication/221920255_High-Speed_VLSI_Architecture_Based_on_Massively_Parallel_Processor_Arrays_for_Real-Time_Remote_Sensing_Applications"
  },
  {
    "id": "castillo-078",
    "title": "High-Speed Stochastic Processes Generator Based on Sum-of-Sinusoids for Channel Emulation",
    "type": "conference-paper",
    "date": "2011-11-01",
    "venue": "ReConFig 2011",
    "authors": "Luis Vela-Garcia, J. Vázquez Castillo, Ramon Parra-Michel, Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1109/reconfig.2011.50",
    "tags": [
      "communications",
      "channel_emulation"
    ],
    "abstractKey": "abstract_castillo_078",
    "researchGateUrl": "https://www.researchgate.net/publication/221437180_High-Speed_Stochastic_Processes_Generator_Based_on_Sum-of-Sinusoids_for_Channel_Emulation"
  },
  {
    "id": "castillo-079",
    "title": "3D-processor arrays accelerators for high-performance computing in remote sensing applications",
    "type": "conference-paper",
    "date": "2011-10-01",
    "venue": "Proceedings of SPIE",
    "authors": "Alejandro Castillo Atoche, J. Vázquez Castillo, Luis Rizo, Jesús Sandoval-Gío",
    "doi": "https://doi.org/10.1117/12.898476",
    "tags": [
      "sensors",
      "hardware",
      "remote_sensing"
    ],
    "abstractKey": "abstract_castillo_079",
    "researchGateUrl": "https://www.researchgate.net/publication/253528860_3D-processor_arrays_accelerators_for_high-performance_computing_in_remote_sensing_applications"
  },
  {
    "id": "castillo-080",
    "title": "An efficient Gaussian random number architecture for MIMO channel emulators",
    "type": "conference-paper",
    "date": "2011-10-01",
    "venue": "IEEE SiPS 2011",
    "authors": "J. Vázquez Castillo, Alejandro Castillo Atoche, Omar Longoria-Gandara, Ramon Parra-Michel",
    "doi": "https://doi.org/10.1109/sips.2011.6088996",
    "tags": [
      "communications",
      "channel_emulation"
    ],
    "abstractKey": "abstract_castillo_080",
    "researchGateUrl": "https://www.researchgate.net/publication/220803750_An_efficient_Gaussian_random_number_architecture_for_MIMO_channel_emulators"
  },
  {
    "id": "castillo-081",
    "title": "Aggregation of parallel computing and hardware/software co-design techniques for high-performance remote sensing applications",
    "type": "conference-paper",
    "date": "2011-07-01",
    "venue": "IEEE IGARSS 2011",
    "authors": "Alejandro Castillo Atoche, Orlando Palma Marrufo, Luis Ricalde Castellanos",
    "doi": "https://doi.org/10.1109/igarss.2011.6048931",
    "tags": [
      "sensors",
      "hardware",
      "remote_sensing",
      "gpu_computing"
    ],
    "abstractKey": "abstract_castillo_081",
    "researchGateUrl": "https://www.researchgate.net/publication/220824917_Aggregation_of_parallel_computing_and_hardwaresoftware_co-design_techniques_for_high-performance_remote_sensing_applications"
  },
  {
    "id": "castillo-082",
    "title": "Wireless Data Acquisition System for Greenhouses",
    "type": "chapter",
    "date": "2011-01-01",
    "venue": "IGI Global",
    "authors": "Raúl Aquino Santos, Victor Rangel, Arthur Edwards-Block, [...], César Rosado Villanueva",
    "doi": "https://doi.org/10.4018/978-1-60960-027-3.ch007",
    "tags": [
      "wireless",
      "environmental"
    ],
    "abstractKey": "abstract_castillo_082",
    "researchGateUrl": "https://www.researchgate.net/publication/344271644_Wireless_Data_Acquisition_System_for_Greenhouses"
  },
  {
    "id": "castillo-083",
    "title": "Towards real time implementation of reconstructive signal processing algorithms using systolic arrays coprocessors",
    "type": "article",
    "date": "2010-08-01",
    "venue": "Journal of Systems Architecture",
    "authors": "Alejandro Castillo Atoche, Deni Torres-Roman, Shkvarko Yuriy",
    "doi": "https://doi.org/10.1016/j.sysarc.2010.05.004",
    "tags": [
      "hardware",
      "signal_processing"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/220339645_Towards_real_time_implementation_of_reconstructive_signal_processing_algorithms_using_systolic_arrays_coprocessors"
  },
  {
    "id": "castillo-084",
    "title": "Experiment Design Regularization-Based Hardware/Software Codesign for Real-Time Enhanced Imaging in Uncertain Remote Sensing Environment",
    "type": "article",
    "date": "2010-02-01",
    "venue": "EURASIP Journal on Advances in Signal Processing",
    "authors": "Alejandro Castillo Atoche, Deni Torres-Roman, Shkvarko Yuriy",
    "doi": "https://doi.org/10.1155/2010/254040",
    "pdfUrl": "https://asp-eurasipjournals.springeropen.com/counter/pdf/10.1155/2010/254040",
    "tags": [
      "sensors",
      "hardware",
      "remote_sensing",
      "image_processing"
    ],
    "abstractKey": "abstract_castillo_084",
    "researchGateUrl": "https://www.researchgate.net/publication/43807984_Experiment_Design_Regularization-Based_HardwareSoftware_Codesign_for_Real-Time_Enhanced_Imaging_in_Uncertain_Remote_Sensing_Environment"
  },
  {
    "id": "castillo-086",
    "title": "Near Real Time Enhancement of Remote Sensing Imagery Based on a Network of Systolic Arrays",
    "type": "chapter",
    "date": "2009-11-01",
    "venue": "Lecture Notes in Computer Science",
    "authors": "Alejandro Castillo Atoche, Deni Torres-Roman, Shkvarko Yuriy",
    "doi": "https://doi.org/10.1007/978-3-642-10268-4_125",
    "tags": [
      "sensors",
      "hardware",
      "remote_sensing",
      "image_processing"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/220842989_Near_Real_Time_Enhancement_of_Remote_Sensing_Imagery_Based_on_a_Network_of_Systolic_Arrays"
  },
  {
    "id": "castillo-087",
    "title": "Control generation for QR decomposition based on the polytope model",
    "type": "article",
    "date": "2009-11-01",
    "venue": "ICEEE 2009",
    "authors": "R. Gomez Ku, Alejandro Castillo Atoche, Deni Torres-Roman",
    "doi": "https://doi.org/10.1109/iceee.2009.5393415",
    "tags": [
      "control_systems",
      "signal_processing"
    ],
    "abstractKey": "abstract_castillo_087",
    "researchGateUrl": "https://www.researchgate.net/publication/251916223_Control_generation_for_QR_decomposition_based_on_the_polytope_model"
  },
  {
    "id": "castillo-088",
    "title": "Reconfigurable architecture of systolic array processors for remote sensing applications",
    "type": "conference-paper",
    "date": "2009-09-01",
    "authors": "Alejandro Castillo Atoche, Deni Torres-Roman, Shkvarko Yuriy",
    "tags": [
      "sensors",
      "hardware",
      "remote_sensing"
    ],
    "abstractKey": "abstract_castillo_088",
    "researchGateUrl": "https://www.researchgate.net/publication/261845905_Reconfigurable_architecture_of_systolic_array_processors_for_remote_sensing_applications"
  },
  {
    "id": "castillo-089",
    "title": "Convex regularization-based hardware/software co-design for real-time enhancement of remote sensing imagery",
    "type": "article",
    "date": "2009-08-01",
    "venue": "Journal of Real-Time Image Processing",
    "authors": "Alejandro Castillo Atoche, Shkvarko Yuriy, Deni Torres-Roman, Hector Perez-Meana",
    "doi": "https://doi.org/10.1007/s11554-009-0115-3",
    "tags": [
      "sensors",
      "hardware",
      "remote_sensing",
      "image_processing"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/225691486_Convex_regularization-based_hardwaresoftware_co-design_for_real-time_enhancement_of_remote_sensing_imagery"
  },
  {
    "id": "castillo-090",
    "title": "Reconfigurable Architecture of Systolic Array Processors for Real Time Remote Sensing Image Enhancement/Reconstruction",
    "type": "article",
    "date": "2009-08-01",
    "authors": "Alejandro Castillo Atoche, Deni Torres-Roman, Shkvarko Yuriy",
    "tags": [
      "sensors",
      "hardware",
      "remote_sensing",
      "image_processing"
    ],
    "abstractKey": "abstract_castillo_090",
    "researchGateUrl": "https://www.researchgate.net/publication/239928898_Reconfigurable_Architecture_of_Systolic_Array_Processors_for_Real_Time_Remote_Sensing_Image_EnhancementReconstruction",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-091",
    "title": "Systolic array implementations for real time enhancement of remote sensing imaging",
    "type": "article",
    "date": "2009-04-01",
    "venue": "SPL 2009",
    "authors": "Alejandro Castillo Atoche, J. Ortegon Aguilar, J. Vázquez Castillo",
    "doi": "https://doi.org/10.1109/spl.2009.4914918",
    "tags": [
      "sensors",
      "hardware",
      "remote_sensing",
      "image_processing"
    ],
    "abstractKey": "abstract_castillo_091",
    "researchGateUrl": "https://www.researchgate.net/publication/251885879_Systolic_array_implementations_for_real_time_enhancement_of_remote_sensing_imaging"
  },
  {
    "id": "castillo-092",
    "title": "Hardware/Software Co-Design for Near Real Time Enhancement of Remote Sensing Imaging",
    "type": "conference-paper",
    "date": "2008-12-01",
    "venue": "ICEEE 2008",
    "authors": "Alejandro Castillo Atoche, Deni Torres-Roman",
    "doi": "https://doi.org/10.1109/iceee.2008.4723418",
    "tags": [
      "sensors",
      "hardware",
      "remote_sensing",
      "image_processing"
    ],
    "abstractKey": "abstract_castillo_092",
    "researchGateUrl": "https://www.researchgate.net/publication/224362369_HardwareSoftware_Co-Design_for_Near_Real_Time_Enhancement_of_Remote_Sensing_Imaging"
  },
  {
    "id": "castillo-093",
    "title": "Enhanced Radar Imaging in Uncertain Environment: A Descriptive Experiment Design Regularization Approach",
    "type": "article",
    "date": "2008-08-01",
    "venue": "International Journal of Navigation and Observation",
    "authors": "Shkvarko Yuriy, Hector Perez-Meana, Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1155/2008/810816",
    "pdfUrl": "https://onlinelibrary.wiley.com/doi/pdfdirect/10.1155/2008/810816",
    "tags": [
      "remote_sensing",
      "image_processing",
      "environmental",
      "optimization"
    ],
    "abstractKey": "abstract_castillo_093",
    "researchGateUrl": "https://www.researchgate.net/publication/26530430_Enhanced_Radar_Imaging_in_Uncertain_Environment_A_Descriptive_Experiment_Design_Regularization_Approach"
  },
  {
    "id": "castillo-094",
    "title": "Laboratory Projects for Engineering Students with FPGA",
    "type": "article",
    "date": "2008-07-01",
    "venue": "IEEE Latin America Transactions",
    "authors": "Alejandro Castillo Atoche, J. Vázquez Castillo, Jaime Silverio Ortegon Aguilar, Carlos Rodriguez Cruz",
    "doi": "https://doi.org/10.1109/tla.2008.4609909",
    "tags": [
      "fpga",
      "education"
    ],
    "abstractKey": "abstract_castillo_094",
    "researchGateUrl": "https://www.researchgate.net/publication/3455209_Laboratory_Projects_for_Engineering_Students_with_FPGA",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-095",
    "title": "FPGA based radar image enhanced: A robust evolutionary controlled filter approach",
    "type": "conference-paper",
    "date": "2008-05-01",
    "venue": "ICCDCS 2008",
    "authors": "Alejandro Castillo Atoche, J. Ortegon Aguilar, J. Vázquez Castillo",
    "doi": "https://doi.org/10.1109/iccdcs.2008.4542629",
    "tags": [
      "fpga",
      "control_systems",
      "remote_sensing",
      "image_processing"
    ],
    "abstractKey": "abstract_castillo_095",
    "researchGateUrl": "https://www.researchgate.net/publication/4340985_FPGA_based_radar_image_enhanced_A_robust_evolutionary_controlled_filter_approach"
  },
  {
    "id": "castillo-096",
    "title": "Towards the Virtual Remote Sensing Laboratory: Intelligent Experiment Design Paradigm",
    "type": "conference-paper",
    "date": "2008-01-01",
    "venue": "IEEE IGARSS 2008",
    "authors": "Shkvarko Yuriy, Hector Perez-Meana, Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1109/igarss.2008.4779976",
    "tags": [
      "sensors",
      "remote_sensing",
      "education"
    ],
    "abstractKey": "abstract_castillo_096",
    "researchGateUrl": "https://www.researchgate.net/publication/220819062_Towards_the_Virtual_Remote_Sensing_Laboratory_Intelligent_Experiment_Design_Paradigm",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-097",
    "title": "Prácticas de laboratorio para estudiantes de ingeniería con FPGA",
    "type": "article",
    "date": "2008-01-01",
    "authors": "Alejandro Castillo Atoche, J. Vázquez Castillo, J Ortegón, C Rodríguez",
    "tags": [
      "fpga"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/228859609_Practicas_de_laboratorio_para_estudiantes_de_ingenieria_con_FPGA",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-098",
    "title": "Visual Programming Platform for Autonomous Mobile Robots Controlled by FPGA's",
    "type": "conference-paper",
    "date": "2007-10-01",
    "venue": "CERMA 2007",
    "authors": "Oscar S. Siordia, J.A. Aldana Iuit, J.R. Atoche Ensenat, [...], Alejandro Castillo Atoche",
    "doi": "https://doi.org/10.1109/cerma.2007.4367742",
    "tags": [
      "fpga",
      "control_systems",
      "robotics"
    ],
    "abstractKey": "abstract_castillo_098",
    "researchGateUrl": "https://www.researchgate.net/publication/4284626_Visual_Programming_Platform_for_Autonomous_Mobile_Robots_Controlled_by_FPGA's"
  },
  {
    "id": "castillo-099",
    "title": "Real time TCP/IP control of modular production systems with FPGAS",
    "type": "article",
    "date": "2007-04-01",
    "venue": "Journal of Applied Research and Technology",
    "authors": "Alejandro Castillo Atoche, J. Vázquez Castillo, Victor M. Sanchez",
    "doi": "https://doi.org/10.22201/icat.16656423.2007.5.01.535",
    "pdfUrl": "http://www.jart.icat.unam.mx/index.php/jart/article/download/535/531",
    "pdfPath": "/pdf/publications/castillo_099.pdf",
    "tags": [
      "fpga",
      "control_systems"
    ],
    "abstractKey": "abstract_castillo_099",
    "researchGateUrl": "https://www.researchgate.net/publication/242278448_Real_time_TCPIP_control_of_modular_production_systems_with_FPGAS"
  },
  {
    "id": "castillo-100",
    "title": "FFT Implementation for Electronic Holograms using Field Programmable Gate Array",
    "type": "conference-paper",
    "date": "2006-05-01",
    "venue": "ICCDCS 2006",
    "authors": "Alejandro Castillo Atoche, Mario Perez-Cortes, J. Vázquez Castillo, J.R. Atoche Ensenat",
    "doi": "https://doi.org/10.1109/iccdcs.2006.250846",
    "tags": [
      "fpga",
      "signal_processing"
    ],
    "abstractKey": "abstract_castillo_100",
    "researchGateUrl": "https://www.researchgate.net/publication/224672752_FFT_Implementation_for_Electronic_Holograms_using_Field_Programmable_Gate_Array"
  },
  {
    "id": "castillo-101",
    "title": "Implementation of MPS Control with FPGAs using Remote Manipulation",
    "type": "conference-paper",
    "date": "2006-05-01",
    "venue": "ICCDCS 2006",
    "authors": "Alejandro Castillo Atoche, J. Vázquez Castillo, Victor M. Sanchez",
    "doi": "https://doi.org/10.1109/iccdcs.2006.250873",
    "tags": [
      "fpga",
      "control_systems"
    ],
    "abstractKey": "abstract_castillo_101",
    "researchGateUrl": "https://www.researchgate.net/publication/224672770_Implementation_of_MPS_Control_with_FPGAs_using_Remote_Manipulation",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-102",
    "title": "Procesamiento de Imágenes con Xilinx System Generator",
    "type": "conference-paper",
    "date": "2006-01-01",
    "authors": "Carlos Rodríguez Cruz, Raziel Rivero Flores, Alejandro Castillo Atoche, J. Vázquez Castillo",
    "tags": [
      "fpga"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/287646815_Procesamiento_de_Imagenes_con_Xilinx_System_Generator"
  },
  {
    "id": "castillo-103",
    "title": "Implementación de la FFT en hologramas generados con FPGAs",
    "type": "article",
    "date": "2006-01-01",
    "venue": "Ingeniería",
    "authors": "Alejandro Castillo Atoche, Mario Perez-Cortes, Mauricio Ortiz-Gutierrez, J. Vázquez Castillo",
    "tags": [
      "fpga",
      "signal_processing"
    ],
    "abstractKey": "abstract_castillo_103",
    "researchGateUrl": "https://www.researchgate.net/publication/287668967_Implementacion_de_la_FFT_en_hologramas_generados_con_FPGAs_Ingenieria"
  },
  {
    "id": "castillo-104",
    "title": "A 2.5 Gbps SONET STS-48/SDH STM-16 Add Drop Multiplexer",
    "type": "article",
    "date": "2002-01-01",
    "authors": "Daylen Torres, Alejandro Castillo Atoche, J Moreno, [...], J R Verdín",
    "tags": [
      "communications"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/228896516_A_25_Gbps_SONET_STS-48SDH_STM-16_Add_Drop_Multiplexer",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-106",
    "title": "SISTEMA REMOTO DE ADQUISICIÓN PARA EL CONTROL DEL RUIDO AMBIENTAL CON FPGA Y FPAA",
    "type": "article",
    "date": null,
    "authors": "Alejandro Castillo Atoche, J. Vázquez Castillo, Victor M. Sanchez, [...], Río Dawn",
    "tags": [
      "fpga",
      "control_systems"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/228728292_SISTEMA_REMOTO_DE_ADQUISICION_PARA_EL_CONTROL_DEL_RUIDO_AMBIENTAL_CON_FPGA_Y_FPAA",
    "researchGateFullTextAvailable": true
  },
  {
    "id": "castillo-107",
    "title": "SYNCHRONIZATION OF CHAOTIC SYSTEMS: FIELD PROGRAMABLE GATE ARRAY AND NONLINEAR CONTROL FEEDBACK APPROACH",
    "type": "article",
    "date": null,
    "authors": "Alejandro Castillo Atoche, Gualberto Solís-Perales, A Matos Gamboa, J.R. Atoche Ensenat",
    "tags": [
      "control_systems"
    ],
    "researchGateUrl": "https://www.researchgate.net/publication/267196819_SYNCHRONIZATION_OF_CHAOTIC_SYSTEMS_FIELD_PROGRAMABLE_GATE_ARRAY_AND_NONLINEAR_CONTROL_FEEDBACK_APPROACH",
    "researchGateFullTextAvailable": true
  }
];

const publicationEnrichment: Record<string, Partial<Publication>> = {
  "castillo-002": { publicationDetails: "287, 111890" },
  "castillo-003": { publicationDetails: "91, 105121", abstractKey: "abstract_castillo_003" },
  "castillo-006": { publicationDetails: "pp. 1–6" },
  "castillo-007": { abstractKey: "abstract_castillo_007" },
  "castillo-009": { publicationDetails: "75, pp. 1–10" },
  "castillo-011": { publicationDetails: "24(19), 6289" },
  "castillo-012": { publicationDetails: "20(9), 10814–10824" },
  "castillo-013": { publicationDetails: "12(8), 124" },
  "castillo-014": {
    publicationDetails: "111, 105526",
    publicAccessUrl: "https://papers.ssrn.com/sol3/papers.cfm?abstract_id=4559015",
    abstractKey: "abstract_castillo_014",
  },
  "castillo-015": { publicationDetails: "20(7), 9235–9243" },
  "castillo-016": { publicationDetails: "16(3), 267–270" },
  "castillo-017": { publicationDetails: "60, 103499", abstractKey: "abstract_castillo_017" },
  "castillo-018": { publicationDetails: "580, 233185", abstractKey: "abstract_castillo_018" },
  "castillo-019": {
    publicationDetails: "32(8), 085008",
    pdfUrl: "https://hal.science/hal-04281415/document",
  },
  "castillo-021": {
    doi: "https://doi.org/10.3390/en16072943",
    publicationDetails: "16(7), 2943",
    pdfUrl: "https://www.mdpi.com/1996-1073/16/7/2943/pdf?version=1679566096",
  },
  "castillo-022": { publicationDetails: "pp. 1–7" },
  "castillo-023": { publicationDetails: "23(3), 2948–2955" },
  "castillo-024": { abstractKey: "abstract_castillo_024" },
  "castillo-025": { publicationDetails: "pp. 225–236" },
  "castillo-027": { publicationDetails: "44(2), 2715–2729" },
  "castillo-028": { publicationDetails: "22(10), 9778–9786" },
  "castillo-030": { publicationDetails: "41(3), 1530–1549", abstractKey: "abstract_castillo_030" },
  "castillo-032": { publicationDetails: "71, pp. 1–10" },
  "castillo-035": {
    doi: "https://doi.org/10.1002/2050-7038.13041",
    publicationDetails: "31(11)",
    abstractKey: "abstract_castillo_035",
  },
  "castillo-036": { publicationDetails: "16(3), 1469–1479", abstractKey: "abstract_castillo_036" },
  "castillo-037": { publicationDetails: "70, pp. 1–10" },
  "castillo-038": { publicationDetails: "21(2), 1965–1974" },
  "castillo-040": { publicationDetails: "31(11), 1416–1429" },
  "castillo-042": { publicationDetails: "18(01), 113–119" },
  "castillo-043": { publicationDetails: "3(11), 1–4" },
  "castillo-046": { publicationDetails: "18(21), 8913–8923" },
  "castillo-047": {
    publicationDetails: "16(6), 1610–1615",
    publicAccessUrl: "http://hdl.handle.net/10251/192551",
  },
  "castillo-048": { publicationDetails: "69, 194–211" },
  "castillo-050": {
    publicationDetails: "pp. 1–2",
    pdfUrl: "https://strathprints.strath.ac.uk/63582/1/Quijano_Centina_etal_SmaGriMet2018_Low_cost_power_systems_metrology_laboratory_based.pdf",
  },
  "castillo-054": { publicationDetails: "pp. 1–4" },
  "castillo-056": { publicationDetails: "pp. 1–6" },
  "castillo-057": { publicationDetails: "120, 389–398", abstractKey: "abstract_castillo_057" },
  "castillo-058": { publicationDetails: "pp. 1–6" },
  "castillo-059": { abstractKey: "abstract_castillo_059" },
  "castillo-061": { abstractKey: "abstract_castillo_061" },
  "castillo-062": {
    publicationDetails: "pp. 1039–1046",
    pdfUrl: "https://link.springer.com/content/pdf/10.1007%2F978-3-319-12568-8_126.pdf",
    abstractKey: "abstract_castillo_062",
  },
  "castillo-063": { publicationDetails: "5(5), 655–666", abstractKey: "abstract_castillo_063" },
  "castillo-064": { publicationDetails: "12(6), 1176–1181" },
  "castillo-066": { publicationDetails: "13(4), 783–796", abstractKey: "abstract_castillo_066" },
  "castillo-068": { publicationDetails: "E97.A(5), 1119–1120" },
  "castillo-069": {
    venue: "IJCCI 2014 – International Joint Conference on Computational Intelligence (SciTePress)",
    publicationDetails: "pp. 193–198",
  },
  "castillo-072": {
    venue: "ICTA 2012 – International Conference on Information and Communication Technologies and Applications (IIIS)",
    pdfUrl: "https://www.iiis.org/CDs2012/CD2012ADII/ICTA_2012/PapersPdf/CT607RP.pdf",
  },
  "castillo-074": { publicationDetails: "pp. 1–4" },
  "castillo-075": {
    venue: "PECCS 2012 – International Conference on Pervasive and Embedded Computing and Communication Systems (SciTePress)",
    publicationDetails: "pp. 327–333",
    pdfUrl: "https://www.scitepress.org/Papers/2012/38352/38352.pdf",
  },
  "castillo-076": { publicationDetails: "32(16), 2197–2205", abstractKey: "abstract_castillo_076" },
  "castillo-078": { publicationDetails: "pp. 315–320" },
  "castillo-079": { publicationDetails: "8183, 818303" },
  "castillo-080": { publicationDetails: "pp. 316–321" },
  "castillo-081": { publicationDetails: "pp. 217–220" },
  "castillo-082": { publicationDetails: "pp. 136–147" },
  "castillo-083": { publicationDetails: "56(8), 327–339", abstractKey: "abstract_castillo_083" },
  "castillo-086": { publicationDetails: "pp. 1070–1077", abstractKey: "abstract_castillo_086" },
  "castillo-087": { publicationDetails: "pp. 1–6" },
  "castillo-089": { publicationDetails: "4(3), 261–272", abstractKey: "abstract_castillo_089" },
  "castillo-090": {
    venue: "WSEAS Transactions on Signal Processing",
    pdfUrl: "http://www.wseas.us/e-library/transactions/signal/2009/29-761.pdf",
  },
  "castillo-091": { publicationDetails: "pp. 59–64" },
  "castillo-092": { publicationDetails: "pp. 525–530" },
  "castillo-094": { publicationDetails: "6(2), 130–136" },
  "castillo-095": { publicationDetails: "pp. 1–5" },
  "castillo-096": { publicationDetails: "vol. 3, p. IV-1328" },
  "castillo-097": { abstractKey: "abstract_castillo_097" },
  "castillo-102": { abstractKey: "abstract_castillo_102" },
  "castillo-104": { abstractKey: "abstract_castillo_104" },
  "castillo-106": { abstractKey: "abstract_castillo_106" },
  "castillo-107": { abstractKey: "abstract_castillo_107" },
  "castillo-098": { publicationDetails: "pp. 537–544" },
  "castillo-100": { publicationDetails: "pp. 115–119" },
  "castillo-101": { publicationDetails: "pp. 275–280" },
};

const enrichedSpreadsheetPublications = spreadsheetPublicationRecords.map(publication => ({
  ...publication,
  ...publicationEnrichment[publication.id],
}));

export const spreadsheetPublications = [
  ...enrichedSpreadsheetPublications,
  ...johanEstradaPublications,
  ...renanQuijanoPublications,
];
